import {createContext, FC, PropsWithChildren, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import ApplicationHeader from '@/components/application-header';
import ContainerCard from '@/components/container-card';
import {createDocumentStore} from '@/components/document/store';
import Spinner from '@/components/spinner';
import {createValueResolver, Model, ValueResolver} from '@/engine';
import {createDependencyMap} from '@/engine/dependencies';
import {useModelQuery} from '@/hooks/queries/use-model-query';
import {SupportedLocale} from '@/initializers/i18next';
import {logger} from '@/initializers/logger';
import {Configuration} from '@/model/types';

export interface ModelContext {
  model: Model;
  domainValueResolver: ValueResolver;
}

const EMPTY_MODEL: Model = {
  version: '',
  jsonVersion: '',
  form: {sections: [], documents: []},
  values: [],
  dependencyMap: createDependencyMap(),
  accessMap: new Map(),
  migrations: [],
};

// eslint-disable-next-line react-refresh/only-export-components
export const ConfigContext = createContext<ModelContext>({
  model: EMPTY_MODEL,
  domainValueResolver: (() => '') as ValueResolver,
});

type Props = PropsWithChildren<{config: Configuration}>;
export const ConfigProvider = ({children, config}: Props) => {
  const {data: model, status, error} = useModelQuery(config);

  if (status === 'error') {
    return <ErrorState error={error} />;
  }

  if (status === 'pending') {
    return <LoadingState />;
  }

  return <ContextState model={model}>{children}</ContextState>;
};

const ErrorState: FC<{error: Error}> = ({error}) => {
  useEffect(() => {
    if (error) {
      logger.error('Error loading configuration:', error);
    }
  }, [error]);

  return (
    <>
      <ApplicationHeader />
      <ContainerCard className="mb-16 grow py-5" data-testid="config-provider-error">
        <ContainerCard.Content className="p-4">
          <div className="mt-12 flex items-center justify-center">
            {/* eslint-disable-next-line i18next/no-literal-string */}
            <div className="text-center text-red-500">Error loading configuration: {error.message}</div>
          </div>
        </ContainerCard.Content>
      </ContainerCard>
    </>
  );
};

const LoadingState: FC = () => (
  <>
    <ApplicationHeader />
    <ContainerCard className="mb-16 grow py-5" data-testid="config-provider-loading">
      <ContainerCard.Content className="p-4">
        <div className="mt-12 flex items-center justify-center">
          <Spinner />
        </div>
      </ContainerCard.Content>
    </ContainerCard>
  </>
);

type ContextStateProps = PropsWithChildren<{model: Model}>;

const ContextState: FC<ContextStateProps> = ({model, children}) => {
  const {i18n} = useTranslation();

  const domainValueResolver = useMemo(() => {
    return createValueResolver(
      createDocumentStore({
        locale: i18n.language as SupportedLocale,
      }),
      model.values,
    );
  }, [i18n.language, model]);

  return (
    <ConfigContext.Provider
      value={{
        model,
        domainValueResolver,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
};
